interface NodeObject {
  [key: string]: any;
}

interface NodeToEdit {
  object: NodeObject;
}

interface RelevantPld {
  id: number;
  [key: string]: any;
}

interface MapContentKeysWithPldKeys {
  [contentType: string]: [string, string][];
}

interface FunctionParams {
  node: {
    modelName: string;
    object: NodeObject;
  };
  changes: NodeToEdit;
  relevantPld: RelevantPld;
  mapContentKeysWithPldKeys: MapContentKeysWithPldKeys;
}

const buildUpdatedProjectLanguageData = ({
  node,
  changes,
  relevantPld,
  mapContentKeysWithPldKeys,
}: FunctionParams) => {
  // Flatten the mapping for quick access
  const keyMap = new Map();
  Object.values(mapContentKeysWithPldKeys).forEach((array) => {
    array.forEach(([pldKey, nodeKey]) => {
      keyMap.set(nodeKey, pldKey);
    });
    if (node.modelName === "content-video-2-d") {
      keyMap.set("source_media", "media_video_id");
    }
  });

  // Initialize the updated data object with the 'id' from relevantPld
  const updatedData = {
    id: relevantPld.id,
  };

  // Transform and map data from 'changes' using the keyMap
  Object.entries(changes).forEach(([key, value]) => {
    if (keyMap.has(key)) {
      updatedData[keyMap.get(key)] = value;
    }
  });

  // content-quizz-image-multiple stores list_answers as
  // [{ text, imageId, is_right }] on the content object, but the PLD column is
  // a plain string[] (the .text projection — shared with content-quizz-3,
  // which already stores strings). Mirror the backend's pldElementTransform
  // (pldElementTransforms.js -> "content-quizz-image-multiples") so the
  // frontend PLD write and the server-side afterUpdate sync agree on the same
  // string[] shape (idempotent in the source language; correct for the
  // target-language text-only edits, where the content PUT never runs).
  if (
    node.modelName === "content-quizz-image-multiple" &&
    Array.isArray(updatedData["list_answers"])
  ) {
    updatedData["list_answers"] = (updatedData["list_answers"] as any[]).map((a) =>
      a && typeof a === "object" ? a.text ?? "" : a,
    );
  }

  // content-quizz-sort stores answers as [{ text, index, id_internal }] on the
  // content object, but the PLD column (list_answer_quizz_sort_data) is the
  // translatable projection [{ text, id_internal }] — `index` (the correct
  // position) is structural and lives only on the content object. Mirror the
  // backend's pldElementTransform (pldElementTransforms.js ->
  // "content-quizz-sorts") so the frontend PLD write and the server-side
  // afterUpdate sync agree on the same shape (idempotent in the source
  // language; correct for target-language text-only edits, where the content
  // PUT never runs).
  if (
    node.modelName === "content-quizz-sort" &&
    Array.isArray(updatedData["list_answer_quizz_sort_data"])
  ) {
    updatedData["list_answer_quizz_sort_data"] = (
      updatedData["list_answer_quizz_sort_data"] as any[]
    ).map((a) =>
      a && typeof a === "object" ? { text: a.text ?? "", id_internal: a.id_internal } : a,
    );
  }

  // content-random-quizz stores questions as the full doubly-nested object
  // array on the content object, but the PLD column
  // (list_question_random_quizz_data) wraps each element under `question`
  // (canonical shape per ghostJsonFields.js; the DeepL deparser reads
  // question.text / answers[].text / winText / loseText). UNLIKE the other
  // quizz types, the PLD keeps the FULL object — it's a wrap, not a text-only
  // projection. NOTE: content-random-quizz is EXCLUDED from the backend
  // afterUpdate PLD sync (src/index.js ~L502-505, alongside content-element-
  // catch), so THIS frontend write is the SOLE PLD writer — there is no
  // server-side sync to agree with. The wrap shape still mirrors
  // pldElementTransforms.js -> "content-random-quizzes" (the canonical shape,
  // also used by the bulk-save / slopify paths and read by the DeepL deparser).
  if (
    node.modelName === "content-random-quizz" &&
    Array.isArray(updatedData["list_question_random_quizz_data"])
  ) {
    updatedData["list_question_random_quizz_data"] = (
      updatedData["list_question_random_quizz_data"] as any[]
    ).map((q) => (q && typeof q === "object" && "question" in q ? q : { question: q }));
  }

  return updatedData;
};

interface BuildAllUpdatedPldsParams {
  node: {
    modelName: string;
    object: NodeObject;
  };
  changes: NodeToEdit;
  activePld: RelevantPld;
  mapContentKeysWithPldKeys: MapContentKeysWithPldKeys;
}

// Phase 3: returns an array of PLD payloads to update.
// For now, only the active-language PLD is written; other-language PLDs are
// left untouched server-side (staleness is computed client-side from timestamps).
export const buildAllUpdatedPlds = ({
  node,
  changes,
  activePld,
  mapContentKeysWithPldKeys,
}: BuildAllUpdatedPldsParams) => {
  return [
    buildUpdatedProjectLanguageData({
      node,
      changes,
      relevantPld: activePld,
      mapContentKeysWithPldKeys,
    }),
  ];
};

export default buildUpdatedProjectLanguageData;
